import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Filter20Regular,
  Search20Regular,
  Warning20Regular,
  CheckmarkCircle20Regular,
  ChevronRight20Regular,
  History20Regular,
  Person20Regular,
  Building20Regular,
  PeopleTeam20Regular,
  DocumentBulletList20Regular,
  ArrowSync20Regular,
  Delete20Regular,
  PersonBoard20Regular,
  Link20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { useAuth } from "../context/AuthContext";
import { VerantwortlichkeitInput } from "../components/verantwortlichkeit";
import type { VerantwortlichkeitValue } from "../components/verantwortlichkeit";
import { ContactRefField } from "../components/relationsInline/ContactRefField";
import type { ContactRef } from "../components/relationsInline/types";
import { PrivateRelationsSection } from "../components/relationsInline/PrivateRelationsSection";
import { CompanyRelationsSection } from "../components/relationsInline/CompanyRelationsSection";
import {
  persistCollectedRelations,
  assignKundenverbindung,
} from "../components/relationsInline/persistRelations";
import { buildMenschenItems } from "../components/verantwortlichkeit/buildMenschenItems";
import { attachMenschen } from "../services/menschenFuersUnternehmenService";
import { CleanupContactEditPanel } from "../components/CleanupContactEditPanel";
import type { CleanupContactEditHandle } from "../components/CleanupContactEditPanel";
import {
  AltkundenStatus,
  AltkundeDetail,
  AltkundeRelationRef,
  ContactStatusOption,
  ClassifyInput,
  NewCompanyInput,
  listAltkunden,
  listAltkundenTeams,
  fetchAltkundeStatuses,
  fetchAltkundeDetail,
  reclassifyAltkunde,
  classifyAltkunde,
  deleteAltkunde,
} from "../services/altkundenCleanupService";
import { bestaetigen } from "../components/redesign/bestaetigen";

const PAGE_SIZE = 30;

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  Kunde: { bg: "#e7f6ec", fg: "#1b5e20" },
  Interessent: { bg: "#f0e6f2", fg: "#4d2051" },
  Altkunde: { bg: "#fdf3d6", fg: "#8a6d00" },
  "Verantwortung fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  // Alter Name desselben Status (bis 29.09.2026).
  "Mensch fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  Haushaltsmitglied: { bg: "#e4f3f1", fg: "#13675a" },
};
const statusChipStyle = (s: string | null | undefined): React.CSSProperties => {
  const c = (s && STATUS_COLORS[s]) || { bg: "#ececec", fg: "#5b5560" };
  return { backgroundColor: c.bg, color: c.fg };
};

const personName = (
  lastname: string | null,
  firstName: string | null,
  id: number,
) => [lastname, firstName].filter(Boolean).join(", ") || `#${id}`;

// Postgres date (YYYY-MM-DD) → DD.MM.YYYY; passes through other formats.
const formatDate = (d: string | null): string => {
  if (!d) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
};

const blankVerant = (): VerantwortlichkeitValue => ({
  isExtern: false,
  roles: [],
  externalCompany: null,
});

// JSON-Array aus einem rel_*-formFields-Key parsen (wie in den Anlage-Sections).
const parseRelArr = (raw: unknown): any[] => {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  filterBar: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  pill: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ...shorthands.padding("6px", "12px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  pillActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  teamSelect: {
    marginLeft: "4px",
    ...shorthands.padding("5px", "8px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    fontSize: "0.82rem",
    fontFamily: BRAND.fontPrimary,
    backgroundColor: "#fff",
    color: BRAND.ink,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("6px", "12px"),
  },
  searchInput: {
    flexGrow: 1,
    appearance: "none",
    ...shorthands.border("0", "solid", "transparent"),
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    outlineStyle: "none",
  },
  card: {
    appearance: "none",
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    ...shorthands.padding("14px", "16px"),
    marginBottom: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  cardIcon: {
    width: "38px",
    height: "38px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
  },
  cardCol: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    flexGrow: 1,
    minWidth: 0,
  },
  cardName: { fontFamily: BRAND.fontAccent, fontSize: "1rem", color: BRAND.ink },
  cardMeta: {
    fontSize: "0.82rem",
    color: BRAND.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  respChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.purple,
    backgroundColor: "#f3eef5",
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "9px"),
    whiteSpace: "nowrap",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  countBadge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.78rem",
    color: BRAND.muted,
    whiteSpace: "nowrap",
  },
  empty: {
    color: BRAND.muted,
    fontStyle: "italic",
    paddingTop: "20px",
    paddingBottom: "20px",
    textAlign: "center",
  },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  successBox: {
    backgroundColor: "#e7f6ec",
    color: "#1b5e20",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#a5d6b7"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  detailHead: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("16px", "18px"),
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  detailHeadRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  detailTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.35rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  statusChip: {
    fontSize: "0.72rem",
    fontWeight: 700,
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "9px"),
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
  },
  trashChip: {
    fontSize: "0.72rem",
    fontWeight: 700,
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "9px"),
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
  },
  extraChip: {
    fontSize: "0.72rem",
    fontWeight: 700,
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "9px"),
    backgroundColor: "#eef3ff",
    color: "#22418a",
  },
  dataGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "10px",
  },
  dataCol: { display: "flex", flexDirection: "column", minWidth: 0 },
  dataLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: BRAND.muted,
  },
  dataValue: { fontSize: "0.92rem", fontWeight: 600, color: BRAND.ink },
  section: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("14px", "18px"),
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  sectionTitle: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.purple,
  },
  relItem: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    ...shorthands.padding("6px", "0"),
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
  },
  relName: { fontWeight: 600, color: BRAND.ink, fontSize: "0.92rem" },
  relMeta: { fontSize: "0.82rem", color: BRAND.muted },
  householdChip: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#1b5e20",
    backgroundColor: "#e7f6ec",
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("1px", "8px"),
  },
  externChip: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#8a6d00",
    backgroundColor: "#fdf3d6",
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("1px", "8px"),
  },
  emptyLine: { fontSize: "0.85rem", color: BRAND.muted, fontStyle: "italic" },
  // „Bestehend"-Einbettung oben in einer Anlege-Karte: read-only Liste der schon
  // vorhandenen Verbindungen, optisch durch eine gestrichelte Linie von der
  // Neuanlage darunter getrennt.
  existingWrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottomWidth: "1px",
    borderBottomStyle: "dashed",
    borderBottomColor: BRAND.borderDashed,
  },
  existingLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.muted,
    marginBottom: "2px",
  },
  actionCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("16px", "18px"),
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.06)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "5px",
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: BRAND.purple,
  },
  eyebrowDanger: { color: "#b3261e" },
  qLabel: { fontWeight: 600, fontSize: "0.96rem", color: BRAND.ink, lineHeight: 1.4 },
  qHint: { fontSize: "0.82rem", color: BRAND.muted, lineHeight: 1.45 },
  reclassifyRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  select: {
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.muted),
    fontSize: "0.9rem",
    fontFamily: BRAND.fontPrimary,
    backgroundColor: "#fff",
    color: BRAND.ink,
    minWidth: "200px",
  },
  yesNoWrap: { display: "flex", columnGap: "8px", flexWrap: "wrap" },
  yesNoBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontWeight: 600,
    fontSize: "0.88rem",
    ...shorthands.padding("7px", "18px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    ":hover": { ...shorthands.borderColor(BRAND.purple), color: BRAND.purple },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  yesNoActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  yesNoActiveDanger: {
    backgroundColor: "#b3261e",
    color: BRAND.white,
    ...shorthands.borderColor("#b3261e"),
    ":hover": { backgroundColor: "#b3261e" },
  },
  subForm: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    ...shorthands.padding("12px", "14px"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  dangerBox: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    ...shorthands.padding("12px", "14px"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#fdecea",
    ...shorthands.border("1px", "solid", "#f3b4ad"),
  },
  fieldLabel: { display: "flex", flexDirection: "column", rowGap: "3px" },
  fieldLabelText: {
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: BRAND.muted,
  },
  reasonInput: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#f3b4ad"),
    outlineStyle: "none",
  },
  btn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnPrimary: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.ink },
  },
  btnDanger: {
    backgroundColor: "#b3261e",
    color: BRAND.white,
    ...shorthands.borderColor("#b3261e"),
    ":hover": { backgroundColor: "#8b1c1c" },
  },
});

const AltkundenCleanupPage = () => {
  const styles = useStyles();
  const goBack = useSmartBack("/datenbereinigung");
  const { hasPermission } = useAuth();

  // List state.
  const [status, setStatus] = useState<AltkundenStatus>("open");
  const [teamId, setTeamId] = useState<string>("");
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>([]);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const genRef = useRef(0);

  // Detail state.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<AltkundeDetail | null>(null);
  const [statuses, setStatuses] = useState<ContactStatusOption[]>([]);
  // SP5 — Status-Flow „Gibt es den Kontakt noch?":
  //  exists === null  → noch nicht beantwortet
  //  exists === true  → JA: Status wählen (Interessent +NKG XOR Kunde)
  //  exists === false → NEIN: löschen (kein Grund, fixer Backend-Default)
  const [exists, setExists] = useState<boolean | null>(null);
  // JA-Zweig: gewählter Ziel-Status. „interessent" optional mit „+ NKG"-Flag
  // (das tatsächliche NKG-Vorgang-Anlegen folgt in SP6 — hier nur Zustand).
  const [statusChoice, setStatusChoice] = useState<"interessent" | "kunde" | null>(null);
  const [nkgFlag, setNkgFlag] = useState(false);
  // Role flow.
  const [menschFuers, setMenschFuers] = useState<boolean | null>(null);
  const [menschIm, setMenschIm] = useState<boolean | null>(null);
  const [targetCompany, setTargetCompany] = useState<ContactRef | null>(null);
  const [verant, setVerant] = useState<VerantwortlichkeitValue>(blankVerant());
  // Neue Verbindungen (O1): nur Neuanlage, gesammelt über rel_*-formFields-Keys
  // (1:1 wie in der Kontaktanlage); bestehende Relationen bleiben read-only.
  const [relFields, setRelFields] = useState<Record<string, string | number | boolean>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionDone, setActionDone] = useState<string | null>(null);
  // SP9 — Abschließen-Orchestrierung: EIN „Speichern & abschließen"-Button unten
  // sequenziert Stammdaten → Verbindungen → Status/NKG bzw. Soft-Delete.
  const [finishing, setFinishing] = useState(false);
  // Ref auf das Stammdaten-Panel: dessen Speichern wird zentral ausgelöst
  // (eigener Panel-Button ist im Abschließen-Kontext ausgeblendet → kein Doppel-Save).
  const editPanelRef = useRef<CleanupContactEditHandle | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    fetchAltkundeStatuses().then(setStatuses).catch(() => undefined);
    listAltkundenTeams().then(setTeams).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    const gen = ++genRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const { rows: data, total: t } = await listAltkunden(
        status,
        teamId || undefined,
        qDebounced || undefined,
        PAGE_SIZE,
        0,
        controller.signal,
      );
      if (gen !== genRef.current) return;
      setRows(data);
      setTotal(t);
      setHasMore(data.length < t);
    } catch (e: any) {
      if (gen === genRef.current && e?.name !== "AbortError")
        setError(String(e?.message ?? e));
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [status, teamId, qDebounced]);

  useEffect(() => {
    if (selectedId == null) load();
    return () => abortRef.current?.abort();
  }, [load, selectedId]);

  const fetchMore = async () => {
    const gen = genRef.current;
    const startLen = rows.length;
    try {
      const { rows: data, total: t } = await listAltkunden(
        status,
        teamId || undefined,
        qDebounced || undefined,
        PAGE_SIZE,
        startLen,
      );
      if (gen !== genRef.current) return;
      setRows((prev) => [...prev, ...data]);
      setTotal(t);
      setHasMore(startLen + data.length < t);
    } catch (e: any) {
      if (gen === genRef.current) {
        setError(String(e?.message ?? e));
        setHasMore(false);
      }
    }
  };

  const openDetail = async (id: number) => {
    setSelectedId(id);
    setDetailLoading(true);
    setDetail(null);
    setActionError(null);
    setActionDone(null);
    setExists(null);
    setStatusChoice(null);
    setNkgFlag(false);
    setMenschFuers(null);
    setMenschIm(null);
    setTargetCompany(null);
    setVerant(blankVerant());
    setRelFields({});
    setFinishing(false);
    try {
      const d = await fetchAltkundeDetail(id);
      setDetail(d);
    } catch (e: any) {
      setActionError(String(e?.message ?? e));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setActionDone(null);
    setActionError(null);
  };

  // Status-Id zu einem Namen (z. B. „Interessent"/„Kunde") aus der Liste auflösen.
  const statusIdByName = useCallback(
    (name: string): number | null => {
      const hit = statuses.find(
        (s) => s.status.trim().toLowerCase() === name.trim().toLowerCase(),
      );
      return hit ? hit.id : null;
    },
    [statuses],
  );

  // SP5/SP6 — JA-Zweig: gewählten Status (Interessent/Kunde) als Primär-Status
  // setzen. Bei „Interessent + NKG" (nkgFlag) legt das Backend zusätzlich einen
  // NKG-Vorgang an (Owner = handelnder User, ohne Frist/Todo, idempotent).
  // Reine Persistenz (wirft bei Fehler); gibt einen Erfolgs-Text zurück. Wird
  // von der Abschließen-Orchestrierung als LETZTER Schritt aufgerufen — NACH
  // Stammdaten + Verbindungen, damit der Backend-Auto-Status die persistierten
  // Relationen sieht.
  const applyStatusStep = async (cid: number): Promise<string> => {
    if (statusChoice == null) {
      throw new Error("Bitte einen Status wählen (Interessent oder Kunde).");
    }
    const targetName = statusChoice === "kunde" ? "Kunde" : "Interessent";
    const targetId = statusIdByName(targetName);
    if (targetId == null) {
      throw new Error(
        `Status „${targetName}" ist nicht verfügbar. Bitte einen Administrator kontaktieren.`,
      );
    }
    const wantNkg = statusChoice === "interessent" && nkgFlag;
    const res = await reclassifyAltkunde(cid, targetId, wantNkg);
    return wantNkg
      ? res.nkgVorgangId
        ? `Status „Interessent“ übernommen. NKG-Vorgang angelegt.`
        : `Status „Interessent“ übernommen. (NKG-Vorgang bereits vorhanden.)`
      : `Status „${targetName}“ übernommen.`;
  };

  // Build a NewCompanyInput from a manually typed ContactRef, or null if existing/empty.
  const newCompanyFrom = (ref: ContactRef | null): NewCompanyInput | undefined => {
    if (!ref || ref.contactId || !ref.lastName?.trim()) return undefined;
    return {
      name: ref.lastName.trim(),
      phone: ref.phone,
      email: ref.email,
      web: ref.web,
      branche: ref.branche,
      legalFormId: ref.legalFormId,
    };
  };
  const companyComplete = (ref: ContactRef | null): boolean => {
    if (!ref) return false;
    if (ref.contactId) return true;
    // Neuanlage: Branche/Rechtsform/Tel/Mail Pflicht (wie Firmen-Anlage).
    return !!(
      ref.lastName?.trim() &&
      ref.branche?.trim() &&
      ref.legalFormId &&
      ref.phone?.trim() &&
      ref.email?.trim()
    );
  };

  // Hat der Nutzer den „Mensch fürs/im Unternehmen"-Block ausgefüllt? (steuert,
  // ob die Orchestrierung classify() aufruft). „fuers" → Ja + Rollen, „im" → Ja.
  const classifyKind = (): "fuers" | "im" | null => {
    if (menschFuers === true) return "fuers";
    if (menschFuers === false && menschIm === true) return "im";
    return null;
  };

  // Baut den ClassifyInput aus dem Mensch-fürs/im-Block und validiert ihn.
  // Wirft (mit deutscher Meldung) bei fehlenden Pflichtangaben — die
  // Orchestrierung fängt das und zeigt es als actionError.
  const buildClassifyInput = (kind: "fuers" | "im"): ClassifyInput => {
    if (!companyComplete(targetCompany)) {
      throw new Error(
        "Bitte eine Firma wählen — bei Neuanlage sind Branche, Rechtsform, Telefon und E-Mail Pflicht.",
      );
    }
    const input: ClassifyInput = {
      targetCompanyId: targetCompany?.contactId,
      targetCompanyNew: newCompanyFrom(targetCompany),
    };
    if (kind === "fuers") {
      if (!verant.roles.length) {
        throw new Error("Bitte mindestens eine Verantwortlichkeit/Rolle wählen.");
      }
      const ext: ContactRef | null =
        verant.isExtern && verant.externalCompany ? verant.externalCompany : null;
      // Bei „extern" ist eine externe Firma Pflicht (sonst keine Spiegel-Relation).
      if (verant.isExtern && !companyComplete(ext)) {
        throw new Error(
          "Externe Firma: bitte wählen — bei Neuanlage sind Branche, Rechtsform, Telefon und E-Mail Pflicht.",
        );
      }
      input.isExtern = verant.isExtern;
      input.roles = verant.roles;
      input.externalCompanyId = ext?.contactId;
      input.externalCompanyNew = newCompanyFrom(ext);
    } else {
      input.isMitarbeiter = true;
      input.isMenschImUnternehmen = true;
    }
    return input;
  };

  const updateRelField = useCallback((name: string, value: string) => {
    setRelFields((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Neue Verbindungen anlegen (O1) — gleicher Persistenz-Pfad wie die Anlage:
  // Menschen fürs Unternehmen über attachMenschen, alles übrige über
  // persistCollectedRelations; Kundenverbindung via assignKundenverbindung.
  // Bestehende Relationen werden NICHT angefasst (kein Update/Delete). Reine
  // Persistenz (wirft bei Fehler) ohne State/Reload — wird von der
  // Abschließen-Orchestrierung VOR dem Status-Schritt aufgerufen, damit der
  // Backend-Auto-Status (SP7) die frisch persistierten Relationen sieht.
  const persistNewRelations = async (cid: number, isFirma: boolean) => {
    if (isFirma) {
      const menschen = parseRelArr(relFields.rel_menschen);
      if (menschen.length) {
        const items = buildMenschenItems(menschen, cid);
        if (items.length) await attachMenschen(items);
      }
      await persistCollectedRelations(
        cid,
        { firmaConn: parseRelArr(relFields.rel_firmaConn) },
        {},
      );
    } else {
      await persistCollectedRelations(
        cid,
        {
          household: parseRelArr(relFields.rel_household),
          weitere: parseRelArr(relFields.rel_weitere),
          personFirma: parseRelArr(relFields.rel_personFirma),
        },
        {},
      );
    }
    const kv = relFields.rel_kvGroupId;
    if (typeof kv === "string" && kv.trim()) {
      await assignKundenverbindung(cid, Number(kv));
    }
  };

  // Ist der untere „Speichern & abschließen"-Button aktivierbar? Erst wenn die
  // Pflicht-Frage „Gibt es den Kontakt noch?" beantwortet ist; im JA-Zweig
  // zusätzlich erst, wenn ein Status (Interessent/Kunde) gewählt wurde.
  const canFinish = (): boolean => {
    if (exists === false) return true; // NEIN → löschen
    if (exists === true) return statusChoice != null; // JA → Status Pflicht
    return false;
  };

  // SP9 — Abschließen-Orchestrierung. EIN Button unten sequenziert den ganzen
  // Bereinigungsvorgang. KEIN doppelter Save-Pfad: die Stammdaten kommen aus dem
  // Panel-Ref (eigener Panel-Button ausgeblendet), die Verbindungen aus dem
  // gesammelten relFields-State (kein separater „anlegen"-Button mehr), der
  // Status über reclassify.
  //
  // Reihenfolge im JA-Zweig (zwingend — Auto-Status liest aus der DB):
  //   1. Stammdaten speichern (Panel) — bricht bei Fehler ab.
  //   2. „Mensch fürs/im Unternehmen" klassifizieren (falls ausgefüllt) → legt
  //      Firmen-Relationen an (classify setzt bereits bereinigtAt/Auto-Status).
  //   3. Neu erfasste Verbindungen persistieren (persistCollectedRelations).
  //   4. Status setzen via reclassify (+NKG) — sieht jetzt alle persistierten
  //      Relationen; Auto-Status bleibt additiv (gewählter Status = Primär).
  // NEIN-Zweig: Soft-Delete (fixer Backend-Grund), Stammdaten/Verbindungen werden
  // bewusst NICHT gespeichert (der Kontakt verschwindet).
  const onFinish = async () => {
    if (!detail) return;
    const c = detail.contact;
    const cid = c.id;
    const isFirma = c.contactType === "Firma";

    // NEIN → löschen (mit Bestätigung).
    if (exists === false) {
      const ok = await bestaetigen({
        titel: "In den Papierkorb verschieben?",
        text: `„${personName(c.lastname, c.firstName, cid)}" wird in den Papierkorb verschoben und lässt sich dort wiederherstellen.`,
        ja: "In den Papierkorb",
        ton: "danger",
      });
      if (!ok) return;
      setFinishing(true);
      setActionError(null);
      setActionDone(null);
      try {
        await deleteAltkunde(cid);
        setActionDone("In den Papierkorb verschoben.");
        setTimeout(() => closeDetail(), 1000);
      } catch (e: any) {
        setActionError(String(e?.message ?? e));
      } finally {
        setFinishing(false);
      }
      return;
    }

    // JA → Stammdaten + Verbindungen + Status sequenzieren.
    if (exists !== true) {
      setActionError("Bitte zuerst „Gibt es den Kontakt noch?“ beantworten.");
      return;
    }
    if (statusChoice == null) {
      setActionError("Bitte einen Status wählen (Interessent oder Kunde).");
      return;
    }
    // „Mensch fürs/im Unternehmen" nur mitnehmen, wenn der Block sauber ausgefüllt
    // ist; den ClassifyInput vorab bauen, damit Validierungsfehler VOR dem
    // Stammdaten-Save auffallen (nichts halb gespeichert).
    const kind = classifyKind();
    let classifyInput: ClassifyInput | null = null;
    if (kind) {
      try {
        classifyInput = buildClassifyInput(kind);
      } catch (e: any) {
        setActionError(String(e?.message ?? e));
        return;
      }
    }

    setFinishing(true);
    setActionError(null);
    setActionDone(null);
    try {
      // 1) Stammdaten (Panel-Save, nur wenn dirty). Bricht bei Fehler ab.
      const savedOk = (await editPanelRef.current?.save()) ?? true;
      if (!savedOk) {
        throw new Error("Stammdaten konnten nicht gespeichert werden.");
      }
      // 2) Mensch fürs/im Unternehmen (legt Firmen-Relationen an).
      if (classifyInput) {
        await classifyAltkunde(cid, classifyInput);
      }
      // 3) Neu erfasste Verbindungen.
      await persistNewRelations(cid, isFirma);
      // 4) Status (+NKG) — sieht jetzt alle persistierten Relationen.
      const statusMsg = await applyStatusStep(cid);
      setActionDone(`Bereinigung abgeschlossen. ${statusMsg}`);
      setTimeout(() => closeDetail(), 1200);
    } catch (e: any) {
      setActionError(String(e?.message ?? e));
    } finally {
      setFinishing(false);
    }
  };

  // Belt-and-suspenders: route is permission-gated (contacts:update), guard the
  // page too. admin/gfgl haben die Permission implizit (hasPermission), CLL/SAH
  // über die Team-Permission contacts:update.
  if (!hasPermission("contacts:update")) return <Navigate to="/access-denied" replace />;

  const renderRelation = (r: AltkundeRelationRef, icon: React.ReactNode) => (
    <div key={r.relationId} className={styles.relItem}>
      <span style={{ color: BRAND.muted, flexShrink: 0 }}>{icon}</span>
      <span className={styles.relName}>
        {personName(r.lastname, r.firstName, r.contactId)}
      </span>
      {(r.relationType || r.function) && (
        <span className={styles.relMeta}>
          {[r.relationType, r.function].filter(Boolean).join(" · ")}
        </span>
      )}
      {r.share && <span className={styles.relMeta}>Anteile: {r.share}</span>}
      {r.isExtern && <span className={styles.externChip}>extern</span>}
      {r.sameHousehold && <span className={styles.householdChip}>Haushalt</span>}
    </div>
  );

  // Read-only Zeilen für bestehende Referenz-Relationen (oder null, wenn leer).
  const relRows = (
    list: AltkundeRelationRef[],
    icon: React.ReactNode,
  ): React.ReactNode =>
    list.length ? <>{list.map((r) => renderRelation(r, icon))}</> : null;

  // Read-only Anzeige einer bestehenden Kundenverbindung (Name + Mitglieder) — null wenn keine.
  const renderKundenverbindungRO = (
    kv: AltkundeDetail["kundenverbindung"],
  ): React.ReactNode => {
    if (!kv) return null;
    return (
      <>
        <div className={styles.relItem}>
          <span style={{ color: BRAND.muted, flexShrink: 0 }}>
            <PeopleTeam20Regular style={{ fontSize: 16 }} />
          </span>
          <span className={styles.relName}>{kv.name ?? `#${kv.id}`}</span>
        </div>
        {kv.members.map((m) => (
          <div key={m.id} className={styles.relItem}>
            <span style={{ color: BRAND.muted, flexShrink: 0 }}>
              {m.contactType === "Firma" ? (
                <Building20Regular style={{ fontSize: 16 }} />
              ) : (
                <Person20Regular style={{ fontSize: 16 }} />
              )}
            </span>
            <span className={styles.relName}>{m.name}</span>
          </div>
        ))}
      </>
    );
  };

  // „Bestehend"-Wrapper zum Einbetten oben in eine Anlege-Karte. null → nichts
  // (dann zeigt die Karte nur die Neuanlage darunter).
  const existingSlot = (rows: React.ReactNode): React.ReactNode =>
    rows ? (
      <div className={styles.existingWrap}>
        <span className={styles.existingLabel}>Bestehend</span>
        {rows}
      </div>
    ) : null;

  // Read-only Gruppierung aller bestehenden Verbindungen — nur für den Papierkorb-
  // Fall (dort gibt es keine Anlege-Karten, in die wir sie einbetten könnten). Im
  // aktiven Fall stehen die Bestehenden via existingSlot oben in den Karten.
  const renderExistingReadonly = (firma: boolean): React.ReactNode => {
    if (!detail) return null;
    const personIcon = <Person20Regular style={{ fontSize: 16 }} />;
    const bldIcon = <Building20Regular style={{ fontSize: 16 }} />;
    const block = (
      title: string,
      titleIcon: React.ReactNode,
      rows: React.ReactNode,
    ): React.ReactNode =>
      rows ? (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>
            {titleIcon} {title}
          </span>
          {rows}
        </div>
      ) : null;
    const household = detail.persons.filter((r) => r.sameHousehold);
    const weitere = detail.persons.filter((r) => !r.sameHousehold);
    return (
      <>
        {firma ? (
          block(
            "Menschen fürs Unternehmen",
            <PersonBoard20Regular style={{ fontSize: 15 }} />,
            relRows(detail.persons, personIcon),
          )
        ) : (
          <>
            {block("Haushalt", <Person20Regular style={{ fontSize: 15 }} />, relRows(household, personIcon))}
            {block(
              "Beziehungen zu Personen",
              <Person20Regular style={{ fontSize: 15 }} />,
              relRows(weitere, personIcon),
            )}
          </>
        )}
        {block(
          "Verbindungen zu Firmen",
          <Building20Regular style={{ fontSize: 15 }} />,
          relRows(detail.companies, bldIcon),
        )}
        {block(
          "Kundenverbindung",
          <PeopleTeam20Regular style={{ fontSize: 15 }} />,
          renderKundenverbindungRO(detail.kundenverbindung),
        )}
      </>
    );
  };

  // SP5 — Status-Flow (A4). Strukturierte Abfrage statt freiem Status-Dropdown:
  //   1. „Gibt es den Kontakt noch?" JA/NEIN.
  //   2. NEIN → Hinweis „wird gelöscht, weil Altkunde" + Soft-Delete (kein Grund).
  //   3. JA   → Status-Auswahl je contactType:
  //        FIRMA : „Interessent + NKG" XOR „Kunde".
  //        PRIVAT: zusätzlich zum (späteren) Auto-Status (SP7) „Interessent" (+NKG)
  //                XOR „Kunde".
  // Das „+ NKG"-Flag (nkgFlag) wird hier nur als Zustand vorbereitet — die
  // NKG-Vorgang-Erzeugung kommt in SP6, die Auto-Status-Ableitung in SP7.
  const renderStatusFlow = (c: AltkundeDetail["contact"]) => {
    const isFirma = c.contactType === "Firma";
    const chooseStatus = (choice: "interessent" | "kunde") => {
      setStatusChoice(choice);
      if (choice === "kunde") setNkgFlag(false);
    };
    return (
      <div className={styles.actionCard}>
        <span className={styles.eyebrow}>
          <ArrowSync20Regular style={{ fontSize: 14 }} /> Status
        </span>
        <span className={styles.qLabel}>Gibt es den Kontakt noch?</span>
        <div className={styles.yesNoWrap}>
          <button
            type="button"
            className={mergeClasses(styles.yesNoBtn, (exists === true) && styles.yesNoActive)}
            onClick={() => {
              setExists(true);
              setStatusChoice(null);
              setNkgFlag(false);
            }}
          >
            Ja
          </button>
          <button
            type="button"
            className={mergeClasses(styles.yesNoBtn, (exists === false) && styles.yesNoActiveDanger)}
            onClick={() => {
              setExists(false);
              setStatusChoice(null);
              setNkgFlag(false);
            }}
          >
            Nein
          </button>
        </div>

        {/* JA → Status-Auswahl (Interessent +NKG XOR Kunde) */}
        {exists === true && (
          <div className={styles.subForm}>
            <span className={styles.qLabel}>Welcher Status?</span>
            {!isFirma && (
              <span className={styles.qHint}>
                Zusätzlich wird — aus den angelegten Verbindungen — automatisch ein
                Status („Verantwortung fürs Unternehmen"/„Haushaltsmitglied") gesetzt.
              </span>
            )}
            <div className={styles.yesNoWrap}>
              <button
                type="button"
                className={mergeClasses(styles.yesNoBtn, (statusChoice === "interessent") && styles.yesNoActive)}
                onClick={() => chooseStatus("interessent")}
              >
                Interessent
              </button>
              <button
                type="button"
                className={mergeClasses(styles.yesNoBtn, (statusChoice === "kunde") && styles.yesNoActive)}
                onClick={() => chooseStatus("kunde")}
              >
                Kunde
              </button>
            </div>

            {/* „+ NKG"-Flag nur bei Interessent. Der NKG-Vorgang wird beim
                Abschließen über reclassify(+nkg) angelegt (SP6). */}
            {statusChoice === "interessent" && (
              <label
                className={styles.fieldLabel}
                style={{ flexDirection: "row", alignItems: "center", columnGap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={nkgFlag}
                  onChange={(e) => setNkgFlag(e.target.checked)}
                />
                <span className={styles.qHint}>
                  + NKG (Nachbearbeitungs-/Kontaktgespräch-Vorgang anlegen)
                </span>
              </label>
            )}
            {/* Kein eigener „Status übernehmen"-Button mehr — das Übernehmen
                erfolgt zentral über „Speichern & abschließen" unten (SP9). */}
          </div>
        )}

        {/* NEIN → Soft-Delete ohne Grund (fixer Backend-Default). Das eigentliche
            Löschen löst der untere „Speichern & abschließen"-Button aus. */}
        {exists === false && (
          <div className={styles.dangerBox}>
            <span className={mergeClasses(styles.eyebrow, styles.eyebrowDanger)}>
              <Delete20Regular style={{ fontSize: 14 }} /> Kontakt löschen
            </span>
            <span className={styles.qHint}>
              Wird gelöscht, weil Altkunde. Beim Abschließen wird der Kontakt in den
              Papierkorb verschoben (wiederherstellbar).
            </span>
          </div>
        )}
      </div>
    );
  };

  // --- Detail render ---
  if (selectedId != null) {
    const c = detail?.contact;
    const isFirma = c?.contactType === "Firma";
    return (
      <div className={styles.root}>
        <button className={styles.backBtn} onClick={closeDetail}>
          <ArrowLeft20Regular /> Zurück zur Liste
        </button>

        {detailLoading ? (
          <p className={styles.empty}>Lade Details…</p>
        ) : detail && c ? (
          <>
            {/* Kopf-Übersicht */}
            <div className={styles.detailHead}>
              <div className={styles.detailHeadRow}>
                <span className={styles.detailTitle}>
                  {personName(c.lastname, c.firstName, c.id)}
                </span>
                <span className={styles.statusChip} style={statusChipStyle(c.statusLabel)}>
                  {c.statusLabel ?? "ohne Status"}
                </span>
                {detail.extraStatuses.map((s) => (
                  <span key={s} className={styles.extraChip}>
                    {s}
                  </span>
                ))}
                {c.deleted && <span className={styles.trashChip}>im Papierkorb</span>}
              </div>
              <div className={styles.dataGrid}>
                <div className={styles.dataCol}>
                  <span className={styles.dataLabel}>Geburtsdatum</span>
                  <span className={styles.dataValue}>{formatDate(c.birthDate)}</span>
                </div>
                <div className={styles.dataCol}>
                  <span className={styles.dataLabel}>Telefon</span>
                  <span className={styles.dataValue}>{c.phone ?? "—"}</span>
                </div>
                <div className={styles.dataCol}>
                  <span className={styles.dataLabel}>E-Mail</span>
                  <span className={styles.dataValue}>{c.email ?? "—"}</span>
                </div>
                <div className={styles.dataCol}>
                  <span className={styles.dataLabel}>Verträge</span>
                  <span className={styles.dataValue}>{c.contractCount}</span>
                </div>
              </div>
            </div>

            {/* Bisherige Informationen — Stammdaten bearbeitbar, immer ausgeklappt
                (nicht einklappbar). Ersetzt die frühere read-only RoFields-Ansicht
                durch das editierbare CleanupContactEditPanel (Person & Firma); dieses
                enthält Anschrift, Kommunikation und Bankverbindung als bearbeitbare
                Karten. SP9: der eigene Speichern-Button des Panels ist hier
                ausgeblendet (hideSaveBar) — gespeichert wird zentral über den
                unteren „Speichern & abschließen"-Button (kein Doppel-Save). */}
            <div className={styles.section}>
              <span className={styles.sectionTitle}>
                <PersonBoard20Regular style={{ fontSize: 15 }} /> Bisherige Informationen
              </span>
              <CleanupContactEditPanel
                key={c.id}
                ref={editPanelRef}
                contactId={c.id}
                hideSaveBar
              />
            </div>

            {/* Rollen-Flow + Status unten + Löschen (nur wenn nicht im Papierkorb).
                Bestehende Verbindungen werden im aktiven Fall direkt in den Anlege-
                Karten unten angezeigt (existing*-Slots); im Papierkorb als read-only
                Gruppierung (dort gibt es keine Karten). */}
            {c.deleted ? (
              <>
                <div className={styles.section}>
                  <span className={styles.emptyLine}>
                    Dieser Kontakt liegt im Papierkorb. Aktionen sind hier deaktiviert —
                    zum Bearbeiten zuerst im Papierkorb wiederherstellen.
                  </span>
                </div>
                {renderExistingReadonly(isFirma)}
              </>
            ) : (
              <>
                {/* Verbindungen und Verknüpfungen. Pro Kategorie EINE Karte: oben die
                    BESTEHENDEN Verbindungen read-only (existing*-Slots), darunter die
                    Neuanlage. FIRMA → CompanyRelationsSection (Menschen fürs
                    Unternehmen / Verbindungen zu Firmen / Kundenverbindung), PRIVAT →
                    PrivateRelationsSection (Haushalt / Beziehungen zu Personen /
                    Verbindungen zu Firmen / Kundenverbindung). Die existing*-Slots
                    sind read-only — Bestehende werden NICHT verändert (das ist O2).
                    SP9: KEIN eigener „anlegen"-Button — der erfasste Stand wird
                    zentral beim Abschließen persistiert (VOR dem Status, damit der
                    Auto-Status ihn sieht). */}
                <div className={styles.section}>
                  <span className={styles.sectionTitle}>
                    <Link20Regular style={{ fontSize: 15 }} /> Verbindungen und Verknüpfungen
                  </span>
                  <span className={styles.qHint}>
                    Bestehende Verbindungen stehen je Karte oben (read-only). Hier neu
                    erfasste werden beim Abschließen unten gespeichert.
                  </span>
                  {isFirma ? (
                    <CompanyRelationsSection
                      formFields={relFields}
                      updateField={updateRelField}
                      existingMenschen={existingSlot(
                        relRows(detail.persons, <Person20Regular style={{ fontSize: 16 }} />),
                      )}
                      existingFirmaConn={existingSlot(
                        relRows(detail.companies, <Building20Regular style={{ fontSize: 16 }} />),
                      )}
                      existingKv={existingSlot(
                        renderKundenverbindungRO(detail.kundenverbindung),
                      )}
                    />
                  ) : (
                    <PrivateRelationsSection
                      formFields={relFields}
                      updateField={updateRelField}
                      existingHousehold={existingSlot(
                        relRows(
                          detail.persons.filter((r) => r.sameHousehold),
                          <Person20Regular style={{ fontSize: 16 }} />,
                        ),
                      )}
                      existingWeitere={existingSlot(
                        relRows(
                          detail.persons.filter((r) => !r.sameHousehold),
                          <Person20Regular style={{ fontSize: 16 }} />,
                        ),
                      )}
                      existingPersonFirma={existingSlot(
                        relRows(detail.companies, <Building20Regular style={{ fontSize: 16 }} />),
                      )}
                      existingKv={existingSlot(
                        renderKundenverbindungRO(detail.kundenverbindung),
                      )}
                    />
                  )}
                </div>

                {/* Mensch fürs/im Unternehmen (nur für Personen) */}
                {!isFirma && (
                  <div className={styles.actionCard}>
                    <span className={styles.eyebrow}>
                      <PersonBoard20Regular style={{ fontSize: 14 }} /> Mensch fürs / im Unternehmen
                    </span>
                    <span className={styles.qLabel}>Ist das ein Mensch fürs Unternehmen?</span>
                    <span className={styles.qHint}>
                      Person, die für eine Firma tätig ist (Geschäftsführer/Gesellschafter/
                      Verantwortlichkeit). Es wird eine Beziehung zur gewählten Firma angelegt.
                    </span>
                    <div className={styles.yesNoWrap}>
                      <button
                        type="button"
                        className={mergeClasses(styles.yesNoBtn, (menschFuers === true) && styles.yesNoActive)}
                        onClick={() => {
                          setMenschFuers(true);
                          setMenschIm(null);
                          setTargetCompany(null);
                          setVerant(blankVerant());
                        }}
                      >
                        Ja
                      </button>
                      <button
                        type="button"
                        className={mergeClasses(styles.yesNoBtn, (menschFuers === false) && styles.yesNoActive)}
                        onClick={() => {
                          setMenschFuers(false);
                          setMenschIm(null);
                          setTargetCompany(null);
                          setVerant(blankVerant());
                        }}
                      >
                        Nein
                      </button>
                    </div>

                    {menschFuers === true && (
                      <div className={styles.subForm}>
                        <label className={styles.fieldLabel}>
                          <span className={styles.fieldLabelText}>Firma, für die der Kontakt tätig ist</span>
                          <ContactRefField
                            wantType="Firma"
                            firmaExtraFields
                            idBase="ak-fuers-firma"
                            value={targetCompany}
                            onChange={setTargetCompany}
                          />
                        </label>
                        <VerantwortlichkeitInput
                          idBase="ak-verant"
                          value={verant}
                          onChange={setVerant}
                          // Die Rechtsform der oben gewählten Firma entscheidet,
                          // welche Organrollen es gibt.
                          legalFormId={targetCompany?.legalFormId}
                        />
                        {/* Kein eigener Speichern-Button — wird beim Abschließen
                            unten zentral mit angelegt (SP9). */}
                      </div>
                    )}

                    {menschFuers === false && (
                      <>
                        <span className={styles.qLabel}>Ist das ein Mensch im Unternehmen?</span>
                        <span className={styles.qHint}>
                          Mitarbeiter/Beschäftigter einer Firma. Es wird eine Mitarbeiter-Beziehung
                          angelegt und der Zusatzstatus „Mensch im Unternehmen" gesetzt.
                        </span>
                        <div className={styles.yesNoWrap}>
                          <button
                            type="button"
                            className={mergeClasses(styles.yesNoBtn, (menschIm === true) && styles.yesNoActive)}
                            onClick={() => setMenschIm(true)}
                          >
                            Ja
                          </button>
                          <button
                            type="button"
                            className={mergeClasses(styles.yesNoBtn, (menschIm === false) && styles.yesNoActive)}
                            onClick={() => setMenschIm(false)}
                          >
                            Nein
                          </button>
                        </div>
                        {menschIm === true && (
                          <div className={styles.subForm}>
                            <label className={styles.fieldLabel}>
                              <span className={styles.fieldLabelText}>Firma (Arbeitgeber)</span>
                              <ContactRefField
                                wantType="Firma"
                                firmaExtraFields
                                idBase="ak-im-firma"
                                value={targetCompany}
                                onChange={setTargetCompany}
                              />
                            </label>
                            {/* Kein eigener Speichern-Button — wird beim Abschließen
                                unten zentral mit angelegt (SP9). */}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Status-Flow (SP5): „Gibt es den Kontakt noch?" JA/NEIN.
                    JA → Status (Interessent +NKG XOR Kunde); NEIN → Soft-Delete
                    ohne Grund (fixer Backend-Default). Ersetzt das frühere freie
                    Status-Dropdown + den separaten Lösch-Block mit Pflicht-Grund. */}
                {renderStatusFlow(c)}

                {/* SP9 — EIN „Speichern & abschließen"-Button GANZ UNTEN. Orchestriert
                    den ganzen Bereinigungsvorgang in fester Reihenfolge:
                      JA  → Stammdaten → (Mensch fürs/im) → Verbindungen → Status (+NKG)
                      NEIN→ Soft-Delete.
                    Es gibt KEINEN konkurrierenden Save-Pfad mehr (Panel-Button,
                    Verbindungen-Button und Status/Lösch-Buttons sind ausgeblendet). */}
                <div className={styles.actionCard}>
                  <span className={styles.eyebrow}>
                    <CheckmarkCircle20Regular style={{ fontSize: 14 }} /> Abschließen
                  </span>
                  <span className={styles.qHint}>
                    {exists === false
                      ? "Der Kontakt wird in den Papierkorb verschoben."
                      : exists === true
                        ? "Stammdaten, neu erfasste Verbindungen und der gewählte Status werden gemeinsam gespeichert."
                        : "Bitte zuerst oben „Gibt es den Kontakt noch?“ beantworten."}
                  </span>
                  <button
                    className={mergeClasses(styles.btn, exists === false ? styles.btnDanger : styles.btnPrimary)}
                    onClick={onFinish}
                    disabled={finishing || !canFinish()}
                    style={{ alignSelf: "flex-start" }}
                  >
                    {exists === false ? <Delete20Regular /> : <CheckmarkCircle20Regular />}
                    {finishing
                      ? "Wird gespeichert…"
                      : exists === false
                        ? "Löschen & abschließen"
                        : "Speichern & abschließen"}
                  </button>
                </div>
              </>
            )}

            {actionError && (
              <div className={styles.errorBox}>
                <Warning20Regular /> {actionError}
              </div>
            )}
            {actionDone && (
              <div className={styles.successBox}>
                <CheckmarkCircle20Regular /> {actionDone}
              </div>
            )}
          </>
        ) : (
          <p className={styles.empty}>Keine Daten.</p>
        )}
      </div>
    );
  }

  // --- List render ---
  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zur Datenbereinigung
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <History20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Altkunden bereinigen</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Kontakte mit Status „Altkunde" ohne offenes Duplikat prüfen: Status ändern,
          als Mensch fürs/im Unternehmen klassifizieren oder löschen.
        </span>
      </div>

      <div className={styles.searchRow}>
        <Search20Regular style={{ color: BRAND.muted }} />
        <input
          className={styles.searchInput}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suche nach Name…"
        />
      </div>

      <div className={styles.filterBar}>
        <Filter20Regular style={{ color: BRAND.muted }} />
        <button
          type="button"
          className={mergeClasses(styles.pill, (status === "open") && styles.pillActive)}
          onClick={() => setStatus("open")}
        >
          Offen
        </button>
        <button
          type="button"
          className={mergeClasses(styles.pill, (status === "bereinigt") && styles.pillActive)}
          onClick={() => setStatus("bereinigt")}
        >
          Bereinigt
        </button>
        {teams.length > 0 && (
          <select
            className={styles.teamSelect}
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            title="Nach Verantwortlichkeit (Team) filtern"
          >
            <option value="">Alle</option>
            <option value="none">Ohne Team</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name ?? t.id}
              </option>
            ))}
          </select>
        )}
        <span style={{ color: BRAND.muted, fontSize: "0.8rem", marginLeft: 8 }}>
          {total} Altkunden
        </span>
      </div>

      {error && (
        <div className={styles.errorBox}>
          <Warning20Regular /> {error}
        </div>
      )}

      <div
        id="scrollableDivAltkunden"
        style={{ height: "calc(100vh - 340px)", overflowY: "auto" }}
      >
        {loading ? (
          <p className={styles.empty}>Lade…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {qDebounced
              ? "Keine Treffer."
              : status === "bereinigt"
                ? "Noch nichts bereinigt."
                : "Keine Altkunden in dieser Ansicht."}
          </p>
        ) : (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={
              <div style={{ textAlign: "center", color: BRAND.muted }}>
                Lade weitere…
              </div>
            }
            scrollableTarget="scrollableDivAltkunden"
            style={{ overflow: "visible" }}
          >
            {rows.map((r) => {
              const meta = [
                r.birthDate ? `geb. ${formatDate(r.birthDate)}` : null,
                r.phone,
                r.email,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <button
                  key={r.id}
                  className={styles.card}
                  onClick={() => openDetail(r.id)}
                >
                  <span className={styles.cardIcon}>
                    <Person20Regular style={{ fontSize: 20 }} />
                  </span>
                  <span className={styles.cardCol}>
                    <span className={styles.cardName}>
                      {personName(r.lastname, r.firstName, r.id)}
                    </span>
                    {meta && (
                      <span className={styles.cardMeta} title={meta}>
                        {meta}
                      </span>
                    )}
                  </span>
                  {r.verantwortlich && (
                    <span className={styles.respChip} title={`Verantwortlich: ${r.verantwortlich}`}>
                      <PersonBoard20Regular style={{ fontSize: 15 }} />
                      {r.verantwortlich}
                    </span>
                  )}
                  <span className={styles.countBadge}>
                    <DocumentBulletList20Regular style={{ fontSize: 14 }} />
                    {r.contractCount} Verträge
                  </span>
                  <ChevronRight20Regular style={{ color: BRAND.muted }} />
                </button>
              );
            })}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default AltkundenCleanupPage;
